import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { List, X, ShieldStar, ShoppingBag, ShareNetwork } from "@phosphor-icons/react";
import { useCart } from "@/lib/cart";
import AnnouncementBar from "@/components/site/AnnouncementBar";

const links = [
  { label: "About", href: "#about" },
  { label: "Plans", href: "#programs" },
  { label: "Add-Ons", href: "#add-ons" },
  { label: "Testimonials", href: "/testimonial" },
  { label: "Events", href: "/events" },
  { label: "Compare", href: "#compare" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

const merchLink = {
  label: "Shop Legacy Merch",
  shortLabel: "Shop",
  href: "/shop",
};

const legacyLink = {
  label: "Legacy Challenge",
  shortLabel: "Legacy Challenge",
  href: "/legacy",
};

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const cart = useCart();
  const onLanding = location.pathname === "/";
  // Prefix hash links with "/" when we're NOT on the landing page so they navigate + scroll.
  // Absolute paths (starting with "/") pass through untouched.
  const anchor = (href) => {
    if (href.startsWith("/")) return href;
    return onLanding ? href : `/${href}`;
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Cart icon should only appear inside the merchandise-shopping experience.
  // Cart state itself is persisted in localStorage regardless of route — leaving
  // the icon off other pages keeps them from feeling like a store page.
  const path = location.pathname || "/";
  const isShopContext =
    path === "/shop" ||
    path.startsWith("/shop/") ||
    path === "/cart" ||
    path.startsWith("/merch/");

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/10" : "bg-[#0A0A0A] border-b border-white/5"
      }`}
    >
      <AnnouncementBar />
      <div className="flex items-center justify-between px-6 md:px-12 lg:px-20 py-5">
        <a href={onLanding ? "#top" : "/"} data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="font-display font-black text-xl tracking-tighter uppercase leading-none">
            Sweat with <span className="font-brush text-[#FF1B91] normal-case ml-0.5 text-2xl">Slay</span>
          </span>
        </a>

        <nav className="hidden xl:flex items-center gap-6">
          {links.map((l) => (
            <a
              key={l.href}
              href={anchor(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-[12px] uppercase tracking-[0.18em] font-medium text-white/70 hover:text-white transition-colors relative group whitespace-nowrap"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#FF1B91] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-2.5 ml-4">
          <a
            href={merchLink.href}
            data-testid="nav-link-merch"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-white/85 hover:text-white border border-white/30 hover:border-white px-3 py-2 transition-all whitespace-nowrap"
          >
            <ShoppingBag size={13} weight="bold" />
            {merchLink.shortLabel}
          </a>
          <a
            href={legacyLink.href}
            data-testid="nav-link-legacy"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-[#0A0A0A] bg-[#C9A24B] hover:bg-[#E4C778] px-3.5 py-2 transition-all whitespace-nowrap"
          >
            <ShieldStar size={13} weight="fill" />
            {legacyLink.shortLabel}
          </a>
          <a
            href="/cart"
            data-testid="nav-cart"
            aria-label="Open cart"
            className="relative inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-white/85 hover:text-white border border-white/30 hover:border-[#FF1B91] px-3 py-2 transition-all"
          >
            <ShoppingBag size={13} weight="bold" />
            {cart.count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1.5 -right-1.5 bg-[#FF1B91] text-white text-[9px] font-bold min-w-[18px] h-[18px] flex items-center justify-center px-1 rounded-full">
                {cart.count}
              </span>
            )}
          </a>
        </div>

        <a
          href="https://invite.everfit.io/Q9TTSYVZYA"
          target="_blank"
          rel="noreferrer noopener"
          data-testid="nav-cta-join"
          className="hidden xl:inline-flex items-center bg-[#FF1B91] text-white px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] btn-brutal btn-brutal-pink hover:bg-[#FF3FA3] ml-2 whitespace-nowrap"
        >
          Join Everfit
        </a>

        <a
          href={legacyLink.href}
          data-testid="nav-tablet-legacy"
          className="hidden md:inline-flex xl:hidden items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-[#0A0A0A] bg-[#C9A24B] hover:bg-[#E4C778] px-3.5 py-2 transition-all whitespace-nowrap"
        >
          <ShieldStar size={13} weight="fill" />
          {legacyLink.shortLabel}
        </a>

        {/* Compact always-visible cart pill — scoped to the shop/merch/cart routes only.
            Cart contents persist across ALL routes (localStorage-backed) but the icon
            is deliberately hidden on non-shop pages so it doesn't float alone in the nav. */}
        {isShopContext && (
          <a
            href="/cart"
            data-testid="nav-cart-compact"
            aria-label="Open cart"
            className="relative xl:hidden inline-flex items-center justify-center w-10 h-10 border border-white/30 hover:border-[#FF1B91] text-white/85 hover:text-white transition-colors ml-2"
          >
            <ShoppingBag size={16} weight="bold" />
            {cart.count > 0 && (
              <span data-testid="nav-cart-count-compact" className="absolute -top-1.5 -right-1.5 bg-[#FF1B91] text-white text-[9px] font-bold min-w-[18px] h-[18px] flex items-center justify-center px-1 rounded-full">
                {cart.count}
              </span>
            )}
          </a>
        )}

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen(!open)}
          className="xl:hidden text-white inline-flex items-center justify-center gap-2 min-h-[44px] -mr-2 ml-2 px-2"
          aria-label="Toggle menu"
        >
          <span className="text-[11px] uppercase tracking-[0.25em] font-bold hidden sm:inline">
            {open ? "Close" : "Menu"}
          </span>
          {open ? <X size={28} weight="bold" /> : <List size={28} weight="bold" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="xl:hidden bg-[#0A0A0A] border-t border-white/10">
          <div className="flex flex-col px-6 py-6 gap-5">
            {/* Primary mobile CTA — top of the hamburger menu */}
            <a
              href={legacyLink.href}
              data-testid="nav-mobile-link-legacy"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.2em] font-bold text-[#0A0A0A] bg-[#C9A24B] hover:bg-[#E4C778] px-5 py-4 transition-all"
            >
              <ShieldStar size={16} weight="fill" />
              {legacyLink.label}
            </a>
            {links.map((l) => (
              <a
                key={l.href}
                href={anchor(l.href)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className="text-base uppercase tracking-[0.15em] font-medium text-white/80"
              >
                {l.label}
              </a>
            ))}
            <a
              href={merchLink.href}
              data-testid="nav-mobile-link-merch"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-2 text-base uppercase tracking-[0.15em] font-bold text-white border-l-2 border-white/30 pl-3 -ml-3"
            >
              <ShoppingBag size={18} weight="bold" />
              {merchLink.label}
            </a>
            <a
              href="#everfit"
              data-testid="nav-mobile-cta-join"
              onClick={() => setOpen(false)}
              className="mt-2 bg-[#FF1B91] text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-center"
            >
              Join Everfit
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
